import { Button, Col, Grid, Inline, Stack } from "@sembark-travel/ui-base"
import {
	Form,
	SelectField,
	SubmissionError,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { useXHR } from "@sembark-travel/xhr"
import { useMemo } from "react"
import * as Validator from "yup"
import { IDocType } from "./store"
import { SelectTripDestination } from "../TripDestinations"

export function NewDocumentTypeItem({
	onSuccess,
	onCancel,
	initialValues,
}: {
	onSuccess: (docType: IDocType) => void
	onCancel?: () => void
	initialValues?: Partial<IDocType>
}) {
	const xhr = useXHR()
	return (
		<NewItemForm
			onCancel={onCancel}
			onSubmit={async (values) => {
				const resp = await xhr.post<{ data: IDocType }>("/document-types", {
					name: values.name?.trim(),
					destinations: values.destinations?.map((des) => des.id) ?? [],
				})
				onSuccess(resp.data.data)
				return resp
			}}
			initialValues={initialValues}
		/>
	)
}

export function NewItemForm({
	onSubmit,
	onCancel,
	initialValues: propInitialValues,
}: {
	onSubmit: (data: Partial<IDocType>) => Promise<unknown>
	onCancel?: () => void
	initialValues?: Partial<IDocType>
}) {
	const initialValues = useMemo<Partial<IDocType>>(
		() => ({
			name: propInitialValues?.name ?? "",
			destinations: propInitialValues?.destinations ?? [],
		}),
		[propInitialValues]
	)

	const validationSchema = validateFormValues(
		Validator.object().shape({
			name: Validator.string().required("Please provide name for the Document"),
		})
	)

	return (
		<Form<Partial<IDocType>>
			initialValues={initialValues}
			validate={validationSchema}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit(values)
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Grid gap="4">
						<Col sm={6}>
							<TextInputField
								label="Name"
								name="name"
								type="text"
								placeholder="e.g. Govt. ID"
								required
							/>
						</Col>
						<Col sm={5}>
							<SelectField
								name="destinations"
								label="Destinations"
								select={SelectTripDestination}
								multiple
								placeholder="Select destinations..."
								fetchOnMount
							/>
						</Col>
						<Col sm={12}>
							<Stack gap="4">
								<SubmissionError />
								<Inline gap="4">
									<Button type="submit" disabled={submitting}>
										{submitting ? "Saving..." : "Save Details"}
									</Button>
									{onCancel ? (
										<Button
											disabled={submitting}
											onClick={onCancel}
											level="tertiary"
										>
											Cancel
										</Button>
									) : null}
								</Inline>
							</Stack>
						</Col>
					</Grid>
				</form>
			)}
		</Form>
	)
}
